import React, { useState, useEffect } from 'react';
import pb from '../lib/pocketbase';
import { Pencil, Trash2, ArrowUp, ArrowDown, Plus, X, GripVertical } from 'lucide-react';
import './AdminCrud.css';

const AdminGallery = () => {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [dragOverItem, setDragOverItem] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        titre: '',
        image: null
    });

    useEffect(() => {
        fetchPhotos();
    }, []);

    const fetchPhotos = async () => {
        setLoading(true);
        try {
            const records = await pb.collection('galerie').getList(1, 100, {
                sort: '+ordre',
            });
            setPhotos(records.items);
        } catch (err) {
            console.error("Error fetching gallery:", err);
            // Fallback sort
            try {
                const records = await pb.collection('galerie').getList(1, 100, {
                    sort: '-created',
                });
                setPhotos(records.items);
            } catch (retryErr) { }
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, files } = e.target;
        if (type === 'file') {
            setFormData({ ...formData, [name]: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const openModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            setFormData({
                titre: item.titre,
                image: null
            });
        } else {
            setEditingItem(null);
            setFormData({
                titre: '',
                image: null
            });
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            const data = new FormData();
            data.append('titre', formData.titre);

            if (formData.image) {
                data.append('image', formData.image);
            }

            if (editingItem) {
                await pb.collection('galerie').update(editingItem.id, data);
            } else {
                // Determine order
                const lastItem = photos[photos.length - 1];
                const newOrder = lastItem && lastItem.ordre ? lastItem.ordre + 1 : photos.length + 1;
                data.append('ordre', newOrder);

                await pb.collection('galerie').create(data);
            }

            closeModal();
            fetchPhotos();
        } catch (err) {
            console.error("Error saving photo:", err);
            alert("Erreur lors de la sauvegarde.");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Supprimer cette photo ?")) {
            try {
                await pb.collection('galerie').delete(id);
                fetchPhotos();
            } catch (err) {
                console.error("Error deleting:", err);
            }
        }
    };

    const handleMove = async (index, direction) => {
        if (direction === 'up' && index === 0) return;
        if (direction === 'down' && index === photos.length - 1) return;

        const otherIndex = direction === 'up' ? index - 1 : index + 1;
        const currentItem = photos[index];
        const otherItem = photos[otherIndex];

        const currentOrder = currentItem.ordre || index + 1;
        const otherOrder = otherItem.ordre || otherIndex + 1;

        try {
            await Promise.all([
                pb.collection('galerie').update(currentItem.id, { ordre: otherOrder }),
                pb.collection('galerie').update(otherItem.id, { ordre: currentOrder })
            ]);
            await fetchPhotos();
        } catch (err) {
            console.error("Error reordering:", err);
        }
    };

    // Drag and Drop Handlers
    const onDragStart = (e, index) => {
        setDraggedItem(photos[index]);
        e.dataTransfer.effectAllowed = "move";
        e.target.style.opacity = '0.5';
    };

    const onDragOver = (e, index) => {
        e.preventDefault();
        setDragOverItem(index);
    };

    const onDragEnd = (e) => {
        e.target.style.opacity = '1';
        setDragOverItem(null);
    };

    const onDrop = async (e, targetIndex) => {
        e.preventDefault();
        const sourceIndex = photos.findIndex(p => p.id === draggedItem.id);
        if (sourceIndex === targetIndex) return;

        const newPhotos = [...photos];
        const [removed] = newPhotos.splice(sourceIndex, 1);
        newPhotos.splice(targetIndex, 0, removed);

        setPhotos(newPhotos);

        try {
            const updates = newPhotos.map((p, idx) =>
                pb.collection('galerie').update(p.id, { ordre: idx + 1 })
            );
            await Promise.all(updates);
            fetchPhotos();
        } catch (err) {
            console.error("Error updating order after drop:", err);
            fetchPhotos();
        }
    };

    if (loading) return <div>Chargement...</div>;

    return (
        <div className="admin-crud-container">
            <div className="admin-tools">
                <button className="btn" onClick={() => openModal()}>
                    <Plus size={16} style={{ marginRight: '5px' }} /> Ajouter une photo
                </button>
            </div>

            <div className="crud-table-wrapper">
                <table className="crud-table" style={{ minWidth: '500px' }}>
                    <thead>
                        <tr>
                            <th style={{ width: '40px' }}></th>
                            <th>Image</th>
                            <th>Titre / Légende</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {photos.map((item, index) => (
                            <tr
                                key={item.id}
                                draggable
                                onDragStart={(e) => onDragStart(e, index)}
                                onDragOver={(e) => onDragOver(e, index)}
                                onDragEnd={onDragEnd}
                                onDrop={(e) => onDrop(e, index)}
                                className={dragOverItem === index ? 'drag-over' : ''}
                            >
                                <td className="drag-handle-cell">
                                    <GripVertical size={18} className="drag-handle-icon" />
                                </td>
                                <td>
                                    {item.image ? (
                                        <img
                                            src={pb.files.getURL(item, item.image, { thumb: '100x100' })}
                                            alt={item.titre}
                                            className="thumbnail"
                                        />
                                    ) : (
                                        <span>No img</span>
                                    )}
                                </td>
                                <td>{item.titre}</td>
                                <td>
                                    <button className="action-btn btn-move" onClick={() => handleMove(index, 'up')} disabled={index === 0}>
                                        <ArrowUp size={14} />
                                    </button>
                                    <button className="action-btn btn-move" onClick={() => handleMove(index, 'down')} disabled={index === photos.length - 1}>
                                        <ArrowDown size={14} />
                                    </button>
                                    <button className="action-btn btn-edit" onClick={() => openModal(item)}>
                                        <Pencil size={14} />
                                    </button>
                                    <button className="action-btn btn-delete" onClick={() => handleDelete(item.id)}>
                                        <Trash2 size={14} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {photos.length === 0 && (
                            <tr>
                                <td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>Galerie vide.</td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Modal */}
                {isModalOpen && (
                    <div className="modal-overlay" onClick={closeModal}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{editingItem ? 'Modifier' : 'Ajouter'} une photo</h2>
                                <button className="close-btn" onClick={closeModal} style={{ border: 'none', background: 'none' }}><X /></button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label>Titre / Légende</label>
                                    <input
                                        type="text"
                                        name="titre"
                                        value={formData.titre}
                                        onChange={handleInputChange}
                                        placeholder="Ex: Vitrine de Noël"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Image</label>
                                    <input
                                        type="file"
                                        name="image"
                                        onChange={handleInputChange}
                                        accept="image/*"
                                    />
                                </div>
                                <div className="modal-actions">
                                    <button type="button" className="btn" style={{ background: '#999' }} onClick={closeModal}>Annuler</button>
                                    <button type="submit" className="btn">Sauvegarder</button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminGallery;
